import { SUPPORTED_TOKENS } from '@/config/tokens';

/**
 * Get token icon URL with fallbacks
 * @param tokenSymbol - Token symbol (e.g., 'ETH', 'LINK')
 * @param size - Icon size (default: 64)
 * @returns Promise<string> - Icon URL
 */
export const getTokenIcon = async (tokenSymbol: string, size: number = 64): Promise<string> => {
  const token = SUPPORTED_TOKENS[tokenSymbol];
  
  if (!token) {
    return generateFallbackIcon(tokenSymbol, size);
  }

  // If we have a local icon path (starts with /), return it directly
  if (token.icon.startsWith('/')) {
    return token.icon;
  }

  // Try CoinGecko API first if we have a coingeckoId
  if (token.coingeckoId) {
    try {
      const coingeckoUrl = `https://api.coingecko.com/api/v3/coins/${token.coingeckoId}`;
      const response = await fetch(coingeckoUrl);
      const data = await response.json();
      
      if (data.image && data.image.small) {
        return data.image.small;
      }
    } catch (error) {
      console.warn(`Failed to fetch icon from CoinGecko for ${tokenSymbol}:`, error);
    }
  }

  // Fallback to direct CoinGecko asset URL
  if (token.icon.includes('coingecko.com')) {
    return token.icon;
  }

  // Final fallback to generated icon
  return generateFallbackIcon(tokenSymbol, size);
};

/**
 * Generate a fallback icon using a placeholder service
 * @param tokenSymbol - Token symbol
 * @param size - Icon size
 * @returns string - Generated icon URL
 */
export const generateFallbackIcon = (tokenSymbol: string, size: number = 64): string => {
  const colors = {
    ETH: { bg: '627EEA', text: 'FFFFFF' },
    LINK: { bg: '375BD2', text: 'FFFFFF' },
    SOL: { bg: '00FFA3', text: '000000' },
    FSPY: { bg: '04AD2F', text: 'FFFFFF' },
    default: { bg: '6B7280', text: 'FFFFFF' }
  };

  const color = colors[tokenSymbol as keyof typeof colors] || colors.default;
  const text = tokenSymbol.toUpperCase(); // Show full symbol, not just first 3 characters
  
  return `https://via.placeholder.com/${size}x${size}/${color.bg}/${color.text}?text=${text}`;
};

/**
 * Cache for token icons to avoid repeated API calls
 */
const iconCache = new Map<string, string>();

/**
 * Get cached token icon or fetch if not cached
 * @param tokenSymbol - Token symbol
 * @param size - Icon size
 * @returns Promise<string> - Cached or fetched icon URL
 */
export const getCachedTokenIcon = async (tokenSymbol: string, size: number = 64): Promise<string> => {
  const cacheKey = `${tokenSymbol}-${size}`;
  
  if (iconCache.has(cacheKey)) {
    return iconCache.get(cacheKey)!;
  }

  const iconUrl = await getTokenIcon(tokenSymbol, size);
  iconCache.set(cacheKey, iconUrl);
  
  return iconUrl;
};

/**
 * Preload token icons for better UX
 * @param tokenSymbols - Array of token symbols to preload
 * @param size - Icon size
 */
export const preloadTokenIcons = async (tokenSymbols: string[], size: number = 64): Promise<void> => {
  const promises = tokenSymbols.map(symbol => getCachedTokenIcon(symbol, size));
  await Promise.all(promises);
};